import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Users,
  Info,
  Plus,
  CheckCircle,
  XCircle,
  Clock,
  LogIn,
  UserX,
  UserCheck,
  Trash2,
  AlertTriangle,
  PauseCircle,
  PlayCircle,
  Pencil,
  X,
  Check,
  Lock,
} from 'lucide-react';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { Button } from '../components/ui/Button.tsx';
import { Badge } from '../components/ui/Badge.tsx';
import { Skeleton } from '../components/ui/Skeleton.tsx';
import { Modal, ModalFooter } from '../components/ui/Modal.tsx';
import { Input, Select } from '../components/ui/Input.tsx';
import { useToast } from '../components/ui/Toast.tsx';
// Email settings tab removed — SES config is now in tenant Integrations page

// ─── Types ────────────────────────────────────────────────────────────────────

interface Tenant {
  id: string;
  slug: string;
  name: string;
  plan: string;
  status: string;
  schemaName: string;
  createdAt: string;
  updatedAt: string;
}

interface TenantUser {
  id: string;
  tenantId: string;
  email: string;
  role: string;
  firstName: string | null;
  lastName: string | null;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

type AddUserForm = {
  email: string;
  password: string;
  role: string;
  firstName: string;
  lastName: string;
};

const EMPTY_USER_FORM: AddUserForm = {
  email: '',
  password: '',
  role: 'operator',
  firstName: '',
  lastName: '',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(d: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function fmtFull(d: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5 border-b border-zinc-50 last:border-0">
      <span className="text-xs text-zinc-500 shrink-0">{label}</span>
      <span className="text-xs text-zinc-800 font-medium text-right break-all">{value ?? '—'}</span>
    </div>
  );
}

const TENANT_STATUS_MAP: Record<string, 'success' | 'danger' | 'warning' | 'default'> = {
  active: 'success',
  suspended: 'warning',
  deleted: 'danger',
};

const PLAN_MAP: Record<string, 'success' | 'info' | 'purple' | 'default'> = {
  enterprise: 'purple',
  pro: 'info',
  standard: 'success',
  free: 'default',
};

const ROLE_MAP: Record<string, 'purple' | 'info' | 'default'> = {
  admin: 'purple',
  operator: 'info',
  viewer: 'default',
};

// ─── Page ─────────────────────────────────────────────────────────────────────

export function TenantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  const [activeTab, setActiveTab] = useState<'overview' | 'users'>('overview');
  const [showAddUser, setShowAddUser] = useState(false);
  const [userForm, setUserForm] = useState<AddUserForm>(EMPTY_USER_FORM);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [showDeleteTenant, setShowDeleteTenant] = useState(false);
  const [editingDetails, setEditingDetails] = useState(false);
  const [detailsForm, setDetailsForm] = useState({ name: '', plan: '' });

  // ── queries ─────────────────────────────────────────────────────────────────

  const { data: tenant, isLoading } = useQuery<Tenant>({
    queryKey: ['tenant', id],
    queryFn: () => api.get(`/sa/tenants/${id}`).then((r) => r.data),
    enabled: !!id,
  });

  const { data: users = [], isLoading: usersLoading } = useQuery<TenantUser[]>({
    queryKey: ['tenant-users', id],
    queryFn: () => api.get(`/sa/tenants/${id}/users`).then((r) => r.data),
    enabled: !!id,
  });

  // ── mutations ────────────────────────────────────────────────────────────────

  const impersonateMutation = useMutation({
    mutationFn: () =>
      api
        .post<{
          token: string;
          tenantId: string;
          tenantName: string;
          tenantSlug: string;
        }>(`/sa/tenants/${id}/impersonate`, {})
        .then((r) => r.data),
    onSuccess: (data) => {
      const base = import.meta.env.VITE_TENANT_BASE_DOMAIN ?? '';
      window.location.href = `https://${data.tenantSlug}.${base}?token=${encodeURIComponent(data.token)}`;
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const addUserMutation = useMutation({
    mutationFn: (body: AddUserForm) => api.post(`/sa/tenants/${id}/users`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant-users', id] });
      setShowAddUser(false);
      setUserForm(EMPTY_USER_FORM);
      toast.success('User added');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const toggleUserMutation = useMutation({
    mutationFn: ({ userId, isActive }: { userId: string; isActive: boolean }) =>
      api.patch(`/sa/tenants/${id}/users/${userId}`, { isActive }).then((r) => r.data),
    onSuccess: (_data, { isActive }) => {
      qc.invalidateQueries({ queryKey: ['tenant-users', id] });
      toast.success(isActive ? 'User enabled' : 'User disabled');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const deleteUserMutation = useMutation({
    mutationFn: (userId: string) => api.delete(`/sa/tenants/${id}/users/${userId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant-users', id] });
      toast.success('User deleted');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const updateTenantStatusMutation = useMutation({
    mutationFn: (status: string) =>
      api.patch<Tenant>(`/sa/tenants/${id}`, { status }).then((r) => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['tenant', id] });
      qc.invalidateQueries({ queryKey: ['admin-tenants'] });
      qc.invalidateQueries({ queryKey: ['admin-tenants-switcher'] });
      if (data.status === 'deleted') {
        toast.success('Tenant deleted');
        navigate('/admin/tenants');
      } else if (data.status === 'suspended') {
        toast.success('Tenant suspended');
      } else if (data.status === 'active') {
        toast.success('Tenant reactivated');
      }
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const updateDetailsMutation = useMutation({
    mutationFn: (body: { name: string; plan: string }) =>
      api.patch<Tenant>(`/sa/tenants/${id}`, body).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant', id] });
      qc.invalidateQueries({ queryKey: ['tenants'] });
      setEditingDetails(false);
      toast.success('Tenant updated');
    },
    onError: (err) => toast.error(extractError(err, 'Failed to update tenant')),
  });

  function openEditDetails() {
    if (!tenant) return;
    setDetailsForm({ name: tenant.name, plan: tenant.plan });
    setEditingDetails(true);
  }

  function cancelEditDetails() {
    setEditingDetails(false);
  }

  function handleSuspend() {
    if (!window.confirm(`Suspend tenant "${tenant?.name}"? Users will not be able to log in.`))
      return;
    updateTenantStatusMutation.mutate('suspended');
  }

  function handleReactivate() {
    if (!window.confirm(`Reactivate tenant "${tenant?.name}"?`)) return;
    updateTenantStatusMutation.mutate('active');
  }

  function handleDeleteTenant() {
    updateTenantStatusMutation.mutate('deleted');
    setShowDeleteTenant(false);
    setDeleteConfirmName('');
  }

  function handleDeleteUser(user: TenantUser) {
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email;
    if (!window.confirm(`Delete user "${name}"? This action cannot be undone.`)) return;
    deleteUserMutation.mutate(user.id);
  }

  // ── loading state ─────────────────────────────────────────────────────────────

  if (isLoading || !tenant) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <Skeleton className="h-48" />
          <Skeleton className="h-48 lg:col-span-2" />
        </div>
      </div>
    );
  }

  const isDeleted = tenant.status === 'deleted';

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/admin/tenants')}
          className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors"
        >
          <ArrowLeft size={16} />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-semibold text-zinc-900 truncate">{tenant.name}</h1>
          <code className="text-[11px] text-zinc-400 font-mono">{tenant.slug}</code>
        </div>
        <Badge variant={PLAN_MAP[tenant.plan] ?? 'default'}>{tenant.plan}</Badge>
        <Badge variant={TENANT_STATUS_MAP[tenant.status] ?? 'default'}>{tenant.status}</Badge>
        {tenant.status === 'active' && (
          <Button
            size="sm"
            loading={impersonateMutation.isPending}
            onClick={() => impersonateMutation.mutate()}
          >
            <LogIn size={13} className="mr-1.5" /> Enter tenant
          </Button>
        )}
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-zinc-100">
        {(['overview', 'users'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-3 py-2 text-xs font-medium capitalize rounded-t-md transition-colors ${
              activeTab === tab
                ? 'text-zinc-900 border-b-2 border-zinc-800 -mb-px'
                : 'text-zinc-500 hover:text-zinc-700'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Email settings moved to tenant Integrations page (SES section) */}

      {/* Overview + Users grid */}
      {(activeTab === 'overview' || activeTab === 'users') && (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left — info */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between gap-2 px-5 py-3.5 border-b border-zinc-100">
              <div className="flex items-center gap-2">
                <Info size={14} className="text-zinc-400" />
                <h2 className="text-sm font-semibold text-zinc-700">Details</h2>
              </div>
              {!isDeleted && !editingDetails && (
                <button
                  onClick={openEditDetails}
                  className="p-1 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-md transition-colors"
                  title="Edit details"
                >
                  <Pencil size={12} />
                </button>
              )}
            </div>
            <div className="p-5 space-y-1">
              {editingDetails ? (
                <>
                  <div className="space-y-3 pb-3">
                    <Input
                      label="Name"
                      value={detailsForm.name}
                      onChange={(e) => setDetailsForm((f) => ({ ...f, name: e.target.value }))}
                      autoFocus
                    />
                    <Select
                      label="Plan"
                      value={detailsForm.plan}
                      onChange={(e) => setDetailsForm((f) => ({ ...f, plan: e.target.value }))}
                    >
                      <option value="standard">Standard</option>
                      <option value="pro">Pro</option>
                      <option value="enterprise">Enterprise</option>
                    </Select>
                  </div>
                  {/* Read-only fields */}
                  <KV
                    label="Status"
                    value={
                      <Badge variant={TENANT_STATUS_MAP[tenant.status] ?? 'default'}>
                        {tenant.status}
                      </Badge>
                    }
                  />
                  <KV
                    label="Slug"
                    value={
                      <span className="flex items-center gap-1">
                        <code className="font-mono text-xs">{tenant.slug}</code>
                        <Lock size={10} className="text-zinc-300 shrink-0" />
                      </span>
                    }
                  />
                  <KV
                    label="DB Schema"
                    value={
                      <span className="flex items-center gap-1">
                        <code className="font-mono text-[10px]">{tenant.schemaName}</code>
                        <Lock size={10} className="text-zinc-300 shrink-0" />
                      </span>
                    }
                  />
                  <KV
                    label="ID"
                    value={<code className="font-mono text-[10px]">{tenant.id}</code>}
                  />
                  {updateDetailsMutation.isError && (
                    <p className="text-xs text-red-500 pt-1">
                      {extractError(updateDetailsMutation.error)}
                    </p>
                  )}
                  <div className="flex items-center justify-end gap-2 pt-3">
                    <button
                      onClick={cancelEditDetails}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-zinc-500 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors"
                    >
                      <X size={12} /> Cancel
                    </button>
                    <button
                      disabled={!detailsForm.name || updateDetailsMutation.isPending}
                      onClick={() => updateDetailsMutation.mutate(detailsForm)}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-white bg-zinc-800 hover:bg-zinc-700 disabled:opacity-40 rounded-lg transition-colors"
                    >
                      <Check size={12} />
                      {updateDetailsMutation.isPending ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <KV
                    label="ID"
                    value={<code className="font-mono text-[10px]">{tenant.id}</code>}
                  />
                  <KV
                    label="Slug"
                    value={<code className="font-mono text-xs">{tenant.slug}</code>}
                  />
                  <KV
                    label="Plan"
                    value={
                      <Badge variant={PLAN_MAP[tenant.plan] ?? 'default'}>{tenant.plan}</Badge>
                    }
                  />
                  <KV
                    label="Status"
                    value={
                      <Badge variant={TENANT_STATUS_MAP[tenant.status] ?? 'default'}>
                        {tenant.status}
                      </Badge>
                    }
                  />
                  <KV
                    label="DB Schema"
                    value={<code className="font-mono text-[10px]">{tenant.schemaName}</code>}
                  />
                  <KV label="Created" value={fmt(tenant.createdAt)} />
                  <KV label="Updated" value={fmt(tenant.updatedAt)} />
                </>
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="mt-4 bg-white border border-zinc-200 rounded-xl p-5">
            <p className="text-xs font-medium text-zinc-600 mb-3 flex items-center gap-1.5">
              <Users size={12} className="text-zinc-400" />
              Users overview
            </p>
            <div className="flex items-end gap-4">
              <div>
                <p className="text-2xl font-semibold text-zinc-900 tabular-nums">{users.length}</p>
                <p className="text-xs text-zinc-400">Total users</p>
              </div>
              <div>
                <p className="text-2xl font-semibold text-emerald-600 tabular-nums">
                  {users.filter((u) => u.isActive).length}
                </p>
                <p className="text-xs text-zinc-400">Active</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right — users */}
        <div className="lg:col-span-2">
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-zinc-100">
              <div className="flex items-center gap-2">
                <Users size={14} className="text-zinc-400" />
                <h2 className="text-sm font-semibold text-zinc-700">Users · {users.length}</h2>
              </div>
              {!isDeleted && (
                <Button
                  size="sm"
                  onClick={() => {
                    setUserForm(EMPTY_USER_FORM);
                    setShowAddUser(true);
                  }}
                >
                  <Plus size={12} className="mr-1" /> Add user
                </Button>
              )}
            </div>

            {usersLoading ? (
              <div className="p-5 space-y-2">
                {[...Array(4)].map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : users.length === 0 ? (
              <div className="p-10 text-center">
                <Users size={24} className="text-zinc-300 mx-auto mb-2" />
                <p className="text-sm text-zinc-400">No users yet</p>
                <p className="text-xs text-zinc-400 mt-1">Add the first user to this tenant.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-zinc-100">
                      <th className="px-5 pb-2 pt-3 text-left text-zinc-400 font-medium">User</th>
                      <th className="px-5 pb-2 pt-3 text-left text-zinc-400 font-medium">Role</th>
                      <th className="px-5 pb-2 pt-3 text-left text-zinc-400 font-medium">Active</th>
                      <th className="px-5 pb-2 pt-3 text-left text-zinc-400 font-medium">
                        Last login
                      </th>
                      <th className="px-5 pb-2 pt-3 text-left text-zinc-400 font-medium">
                        Created
                      </th>
                      {!isDeleted && (
                        <th className="px-5 pb-2 pt-3 text-left text-zinc-400 font-medium">
                          Actions
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-50">
                    {users.map((u) => {
                      const isTogglingThis =
                        toggleUserMutation.isPending &&
                        (toggleUserMutation.variables as { userId: string })?.userId === u.id;
                      const isDeletingThis =
                        deleteUserMutation.isPending && deleteUserMutation.variables === u.id;
                      return (
                        <tr key={u.id} className="hover:bg-zinc-50 transition-colors">
                          <td className="px-5 py-2.5">
                            <div>
                              <p className="font-medium text-zinc-800">
                                {[u.firstName, u.lastName].filter(Boolean).join(' ') || u.email}
                              </p>
                              <p className="text-[11px] text-zinc-400">{u.email}</p>
                            </div>
                          </td>
                          <td className="px-5 py-2.5">
                            <Badge variant={ROLE_MAP[u.role] ?? 'default'}>{u.role}</Badge>
                          </td>
                          <td className="px-5 py-2.5">
                            {u.isActive ? (
                              <CheckCircle size={14} className="text-emerald-500" />
                            ) : (
                              <XCircle size={14} className="text-red-400" />
                            )}
                          </td>
                          <td className="px-5 py-2.5 text-zinc-500 whitespace-nowrap">
                            {u.lastLoginAt ? (
                              <span className="flex items-center gap-1">
                                <Clock size={10} className="text-zinc-300" />
                                {fmtFull(u.lastLoginAt)}
                              </span>
                            ) : (
                              <span className="text-zinc-300">Never</span>
                            )}
                          </td>
                          <td className="px-5 py-2.5 text-zinc-500 whitespace-nowrap">
                            {fmt(u.createdAt)}
                          </td>
                          {!isDeleted && (
                            <td className="px-5 py-2.5">
                              <div className="flex items-center gap-1">
                                {/* Toggle active */}
                                <button
                                  disabled={isTogglingThis}
                                  onClick={() =>
                                    toggleUserMutation.mutate({
                                      userId: u.id,
                                      isActive: !u.isActive,
                                    })
                                  }
                                  title={u.isActive ? 'Disable user' : 'Enable user'}
                                  className="p-1 rounded text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 disabled:opacity-40 transition-colors"
                                >
                                  {u.isActive ? <UserX size={13} /> : <UserCheck size={13} />}
                                </button>
                                {/* Hard delete */}
                                <button
                                  disabled={isDeletingThis}
                                  onClick={() => handleDeleteUser(u)}
                                  title="Delete user"
                                  className="p-1 rounded text-zinc-400 hover:bg-red-50 hover:text-red-500 disabled:opacity-40 transition-colors"
                                >
                                  <Trash2 size={13} />
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
      )}

      {/* ── Danger Zone ───────────────────────────────────────────────────────── */}
      {!isDeleted && (
        <div className="bg-white border border-red-200 rounded-xl overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3.5 border-b border-red-100 bg-red-50/50">
            <AlertTriangle size={14} className="text-red-400" />
            <h2 className="text-sm font-semibold text-red-700">Danger Zone</h2>
          </div>
          <div className="divide-y divide-zinc-100">
            {/* Suspend / Reactivate */}
            {tenant.status === 'active' ? (
              <div className="flex items-center justify-between px-5 py-4 gap-4">
                <div>
                  <p className="text-sm font-medium text-zinc-800">Suspend tenant</p>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    Blocks all users from logging in. The tenant data is preserved.
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  loading={
                    updateTenantStatusMutation.isPending &&
                    updateTenantStatusMutation.variables === 'suspended'
                  }
                  onClick={handleSuspend}
                  className="border border-amber-200 text-amber-700 hover:bg-amber-50 shrink-0"
                >
                  <PauseCircle size={13} className="mr-1.5" /> Suspend
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-between px-5 py-4 gap-4">
                <div>
                  <p className="text-sm font-medium text-zinc-800">Reactivate tenant</p>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    Restores access for all users of this tenant.
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  loading={
                    updateTenantStatusMutation.isPending &&
                    updateTenantStatusMutation.variables === 'active'
                  }
                  onClick={handleReactivate}
                  className="border border-emerald-200 text-emerald-700 hover:bg-emerald-50 shrink-0"
                >
                  <PlayCircle size={13} className="mr-1.5" /> Reactivate
                </Button>
              </div>
            )}

            {/* Delete */}
            <div className="flex items-center justify-between px-5 py-4 gap-4">
              <div>
                <p className="text-sm font-medium text-zinc-800">Delete tenant</p>
                <p className="text-xs text-zinc-500 mt-0.5">
                  Marks the tenant as deleted. The schema and data are retained but inaccessible.
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDeleteTenant(true)}
                className="border border-red-200 text-red-600 hover:bg-red-50 shrink-0"
              >
                <Trash2 size={13} className="mr-1.5" /> Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add user modal */}
      <Modal open={showAddUser} onClose={() => setShowAddUser(false)} title="Add User">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="First name"
              value={userForm.firstName}
              onChange={(e) => setUserForm((f) => ({ ...f, firstName: e.target.value }))}
              placeholder="Mario"
            />
            <Input
              label="Last name"
              value={userForm.lastName}
              onChange={(e) => setUserForm((f) => ({ ...f, lastName: e.target.value }))}
              placeholder="Rossi"
            />
          </div>
          <Input
            label="Email"
            type="email"
            value={userForm.email}
            onChange={(e) => setUserForm((f) => ({ ...f, email: e.target.value }))}
            placeholder="mario@example.com"
          />
          <Input
            label="Password"
            type="password"
            value={userForm.password}
            onChange={(e) => setUserForm((f) => ({ ...f, password: e.target.value }))}
            placeholder="••••••••"
          />
          <Select
            label="Role"
            value={userForm.role}
            onChange={(e) => setUserForm((f) => ({ ...f, role: e.target.value }))}
          >
            <option value="admin">Admin</option>
            <option value="operator">Operator</option>
            <option value="viewer">Viewer</option>
          </Select>
          <ModalFooter>
            <Button variant="ghost" size="sm" onClick={() => setShowAddUser(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              loading={addUserMutation.isPending}
              disabled={!userForm.email || !userForm.password}
              onClick={() => addUserMutation.mutate(userForm)}
            >
              Add user
            </Button>
          </ModalFooter>
        </div>
      </Modal>

      {/* Delete tenant confirm modal */}
      <Modal
        open={showDeleteTenant}
        onClose={() => {
          setShowDeleteTenant(false);
          setDeleteConfirmName('');
        }}
        title="Delete tenant"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-100 rounded-lg">
            <AlertTriangle size={15} className="text-red-500 mt-0.5 shrink-0" />
            <p className="text-xs text-red-700">
              This will mark <strong>{tenant.name}</strong> as deleted. All users will lose access
              immediately. The tenant data is preserved in the database but will be inaccessible.
            </p>
          </div>
          <Input
            label={`Type "${tenant.name}" to confirm`}
            value={deleteConfirmName}
            onChange={(e) => setDeleteConfirmName(e.target.value)}
            placeholder={tenant.name}
          />
          <ModalFooter>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowDeleteTenant(false);
                setDeleteConfirmName('');
              }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={deleteConfirmName !== tenant.name}
              loading={updateTenantStatusMutation.isPending}
              onClick={handleDeleteTenant}
              className="bg-red-600 hover:bg-red-700 text-white border-red-600"
            >
              Delete tenant
            </Button>
          </ModalFooter>
        </div>
      </Modal>
    </div>
  );
}
